/**
 * Platform-specific options panel.
 *
 * Renders a section per selected platform containing only that platform's
 * relevant controls. Adding a new platform means adding one more case below.
 */

import { ChevronDown, ChevronUp, Upload } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import type { TranslationKey } from '../../../i18n';
import { PLATFORM_CONFIGS } from '../../../config/platforms';
import type { PlatformId } from '../../../config/platforms';
import type { AllPlatformOptions } from '../../../types/composer';
import { PlatformBadge } from './PlatformBadge';

interface PlatformOptionsProps {
  selectedPlatforms: PlatformId[];
  options: AllPlatformOptions;
  onChange: <P extends PlatformId>(platform: P, opts: Partial<AllPlatformOptions[P]>) => void;
  onYouTubeThumbnail: (file: File | null) => void;
}

export function PlatformOptions({
  selectedPlatforms,
  options,
  onChange,
  onYouTubeThumbnail,
}: Readonly<PlatformOptionsProps>) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<PlatformId>>(new Set(selectedPlatforms));

  const toggle = (pid: PlatformId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) {
        next.delete(pid);
      } else {
        next.add(pid);
      }
      return next;
    });
  };

  if (selectedPlatforms.length === 0) return null;

  return (
    <div className="space-y-2">
      {selectedPlatforms.map((pid) => {
        const cfg = PLATFORM_CONFIGS[pid];
        const isExpanded = expanded.has(pid);

        return (
          <div
            key={pid}
            className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
          >
            {/* Accordion header */}
            <button
              type="button"
              onClick={() => toggle(pid)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <PlatformBadge platform={pid} size="sm" />
              <span className="text-sm font-semibold text-slate-800 flex-1 text-left">
                {cfg.name} {t('composer.options.suffix')}
              </span>
              {isExpanded ? (
                <ChevronUp size={15} className="text-slate-400" />
              ) : (
                <ChevronDown size={15} className="text-slate-400" />
              )}
            </button>

            {/* Accordion body */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                {pid === 'facebook' && (
                  <FacebookOptionsPanel
                    opts={options.facebook}
                    onChange={(o) => onChange('facebook', o)}
                    t={t}
                  />
                )}
                {pid === 'instagram' && (
                  <InstagramOptionsPanel
                    opts={options.instagram}
                    onChange={(o) => onChange('instagram', o)}
                    t={t}
                  />
                )}
                {pid === 'linkedin' && (
                  <LinkedInOptionsPanel
                    opts={options.linkedin}
                    onChange={(o) => onChange('linkedin', o)}
                    t={t}
                  />
                )}
                {pid === 'youtube' && (
                  <YouTubeOptionsPanel
                    opts={options.youtube}
                    onChange={(o) => onChange('youtube', o)}
                    onThumbnail={onYouTubeThumbnail}
                    t={t}
                  />
                )}
                {pid === 'x' && (
                  <XOptionsPanel
                    opts={options.x}
                    onChange={(o) => onChange('x', o)}
                    t={t}
                  />
                )}
                {pid === 'threads' && (
                  <div className="text-sm text-slate-400">{t('composer.options.noOptions')}</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Per-platform option panels ────────────────────────────────────────────────

type TranslateFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;

// Facebook
interface FacebookOptionsPanelProps {
  opts: AllPlatformOptions['facebook'];
  onChange: (o: Partial<AllPlatformOptions['facebook']>) => void;
  t: TranslateFn;
}
function FacebookOptionsPanel({ opts, onChange, t }: Readonly<FacebookOptionsPanelProps>) {
  return (
    <div className="space-y-3">
      <Toggle
        label={t('composer.options.fb.disableComments')}
        checked={opts.disableComments}
        onChange={(v) => onChange({ disableComments: v })}
      />
    </div>
  );
}

// Instagram
interface InstagramOptionsPanelProps {
  opts: AllPlatformOptions['instagram'];
  onChange: (o: Partial<AllPlatformOptions['instagram']>) => void;
  t: TranslateFn;
}
function InstagramOptionsPanel({ opts, onChange, t }: Readonly<InstagramOptionsPanelProps>) {
  return (
    <div className="space-y-3">
      {/* Post type selector */}
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">
          {t('composer.options.ig.postType')}
        </label>
        <div className="flex gap-1.5">
          {(['feed', 'reel', 'story'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => onChange({ postType: type })}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors',
                opts.postType === type
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              )}
            >
              {t(`composer.options.ig.${type}`)}
            </button>
          ))}
        </div>
      </div>

      {/* First comment */}
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">
          {t('composer.options.ig.firstComment')}
        </label>
        <textarea
          value={opts.firstComment}
          onChange={(e) => onChange({ firstComment: e.target.value })}
          placeholder={t('composer.options.ig.firstCommentPlaceholder')}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
          rows={2}
          maxLength={2200}
        />
      </div>

      {/* Location */}
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">
          {t('composer.options.ig.location')}
        </label>
        <input
          type="text"
          value={opts.location}
          onChange={(e) => onChange({ location: e.target.value })}
          placeholder={t('composer.options.ig.locationPlaceholder')}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}

// LinkedIn
interface LinkedInOptionsPanelProps {
  opts: AllPlatformOptions['linkedin'];
  onChange: (o: Partial<AllPlatformOptions['linkedin']>) => void;
  t: TranslateFn;
}
function LinkedInOptionsPanel({ opts, onChange, t }: Readonly<LinkedInOptionsPanelProps>) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block">
        {t('composer.options.li.accountType')}
      </label>
      <div className="flex gap-1.5">
        {(['personal', 'company'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onChange({ accountType: type })}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors',
              opts.accountType === type
                ? 'bg-sky-100 text-sky-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            )}
          >
            {t(`composer.options.li.${type}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

// YouTube
interface YouTubeOptionsPanelProps {
  opts: AllPlatformOptions['youtube'];
  onChange: (o: Partial<AllPlatformOptions['youtube']>) => void;
  onThumbnail: (file: File | null) => void;
  t: TranslateFn;
}
function YouTubeOptionsPanel({ opts, onChange, onThumbnail, t }: Readonly<YouTubeOptionsPanelProps>) {
  const thumbInputRef = useRef<HTMLInputElement>(null);

  const handleThumbChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      onThumbnail(file);
      e.target.value = '';
    },
    [onThumbnail]
  );

  return (
    <div className="space-y-3">
      <Field label={t('composer.options.yt.title')} required>
        <input
          type="text"
          value={opts.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder={t('composer.options.yt.titlePlaceholder')}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          maxLength={100}
        />
        <span className="text-[11px] text-slate-400 text-right block">{opts.title.length}/100</span>
      </Field>

      <Field label={t('composer.options.yt.tags')}>
        <input
          type="text"
          value={opts.tags}
          onChange={(e) => onChange({ tags: e.target.value })}
          placeholder={t('composer.options.yt.tagsPlaceholder')}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </Field>

      <Field label={t('composer.options.yt.visibility')}>
        <select
          value={opts.visibility}
          onChange={(e) => onChange({ visibility: e.target.value as 'public' | 'private' | 'unlisted' })}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
        >
          <option value="public">{t('composer.options.yt.public')}</option>
          <option value="private">{t('composer.options.yt.private')}</option>
          <option value="unlisted">{t('composer.options.yt.unlisted')}</option>
        </select>
      </Field>

      {/* Thumbnail */}
      <Field label={t('composer.options.yt.thumbnail')}>
        <input
          ref={thumbInputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="sr-only"
          onChange={handleThumbChange}
        />
        {opts.thumbnailPreviewUrl ? (
          <div className="relative inline-block">
            <img
              src={opts.thumbnailPreviewUrl}
              alt="thumbnail"
              className="w-32 h-20 object-cover rounded-xl border border-slate-200"
            />
            <button
              type="button"
              onClick={() => onThumbnail(null)}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center text-xs hover:bg-rose-600"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => thumbInputRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 hover:border-slate-400 hover:bg-slate-50 transition-colors"
          >
            <Upload size={14} />
            {t('composer.options.yt.uploadThumb')}
          </button>
        )}
      </Field>
    </div>
  );
}

// X (Twitter)
interface XOptionsPanelProps {
  opts: AllPlatformOptions['x'];
  onChange: (o: Partial<AllPlatformOptions['x']>) => void;
  t: TranslateFn;
}
function XOptionsPanel({ opts, onChange, t }: Readonly<XOptionsPanelProps>) {
  return (
    <div className="space-y-3">
      <Toggle
        label={t('composer.options.x.threadMode')}
        description={t('composer.options.x.threadModeDesc')}
        checked={opts.threadMode}
        onChange={(v) => onChange({ threadMode: v })}
      />
      <Toggle
        label={t('composer.options.x.sensitive')}
        checked={opts.sensitiveContent}
        onChange={(v) => onChange({ sensitiveContent: v })}
      />
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer group">
      <div>
        <span className="text-sm text-slate-800 font-medium">{label}</span>
        {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          checked ? 'bg-brand-600' : 'bg-slate-200'
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
    </label>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
